import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, X, Folder, File, ArrowRight, Loader2, ScanSearch, Type, HardDrive, FileText, FolderOpen, RefreshCw, Trash2, MapPin, ExternalLink } from 'lucide-react';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { FolderSizeResult } from '../../api/IFileSystem';
import { t } from '../../i18n';
import { eventBus } from '../../../../core/EventBus';

// Format file size to human readable format
const formatFileSize = (bytes: number | undefined): string => {
  if (bytes === undefined || bytes === null) return '-';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return size.toFixed(i > 0 ? 1 : 0) + ' ' + units[i];
};

// Get file extension from filename
const getFileExtension = (name: string): string => {
  const lastDot = name.lastIndexOf('.');
  if (lastDot === -1 || lastDot === 0) return '';
  return name.substring(lastDot + 1).toLowerCase();
};

const GlobalSearchPanel: React.FC = () => {
  const {
    isGlobalSearchOpen,
    closeGlobalSearch,
    globalSearchMode,
    setGlobalSearchMode,
    globalSearchQuery,
    setGlobalSearchQuery,
    globalSearchResults,
    globalSearchLoading,
    globalSearchError,
    globalSearchStatus,
    globalSearchTruncated,
    executeGlobalSearch,
    navigateToSearchResult,
    openSearchEngineInitModal,
    fs,
  } = useFileManagerStore();

  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [debounceTimer, setDebounceTimer] = useState<number | null>(null);
  const mouseDownOutsideRef = useRef(false);

  // Transient notice for remote-desktop open actions (shown briefly in footer)
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const actionNoticeTimer = useRef<number | null>(null);

  // Open a search result on the remote desktop: 'select' locates it in Explorer,
  // 'open' launches it with the default program (or opens the folder).
  const handleRemoteOpen = useCallback(async (fullPath: string, mode: 'open' | 'select') => {
    if (!fs?.shellOpen) {
      setActionNotice('当前连接不支持远程打开');
      return;
    }
    setActionNotice(mode === 'select' ? '正在远程定位…' : '正在远程打开…');
    const res = await fs.shellOpen(fullPath, mode);
    if (actionNoticeTimer.current) window.clearTimeout(actionNoticeTimer.current);
    if (res.ok) {
      // shell 命令执行成功：关闭搜索窗口，并关闭文件管理器窗口（结果已呈现在远程桌面）
      closeGlobalSearch();
      eventBus.emit('fm:close', undefined as never);
      return;
    }
    setActionNotice(`远程打开失败：${res.message || '未知错误'}`);
    actionNoticeTimer.current = window.setTimeout(() => setActionNotice(null), 3000);
  }, [fs, closeGlobalSearch]);

  // Filter state
  const [selectedVolumes, setSelectedVolumes] = useState<Set<string>>(new Set());
  const [selectedTypes, setSelectedTypes] = useState<Set<'file' | 'folder'>>(new Set());
  const [selectedExtensions, setSelectedExtensions] = useState<Set<string>>(new Set());

  // Folder sizes state (fetched via getFolderSizes API)
  const [folderSizes, setFolderSizes] = useState<Record<string, FolderSizeResult>>({});

  // Initialized volumes (fetched from search engine status)
  const [initializedVolumes, setInitializedVolumes] = useState<string[]>([]);

  // Reset filters and folder sizes when search results change
  useEffect(() => {
    setSelectedVolumes(new Set());
    setSelectedTypes(new Set());
    setSelectedExtensions(new Set());
    setFolderSizes({});
  }, [globalSearchResults.length]);

  // Fetch folder sizes for search results (same approach as file list view)
  useEffect(() => {
    if (globalSearchResults.length === 0) return;
    if (!fs || typeof (fs as any).getFolderSizes !== 'function') return;

    const folders = globalSearchResults.filter(r => r.is_dir && r.full_path);
    if (folders.length === 0) return;

    const fetchSizes = async () => {
      try {
        const results = await (fs as any).getFolderSizes(
          folders.map(f => ({ path: f.full_path }))
        );
        setFolderSizes(results);
      } catch (e) {
        console.error('[GlobalSearch] Failed to fetch folder sizes:', e);
      }
    };

    fetchSizes();
  }, [globalSearchResults, fs]);

  // Compute available filter options from results
  const filterOptions = useMemo(() => {
    const volumes = new Set<string>();
    const extensionCounts = new Map<string, number>();
    let fileCount = 0;
    let folderCount = 0;

    globalSearchResults.forEach(result => {
      // Volumes
      if (result.volume) {
        volumes.add(result.volume);
      }

      // File/Folder counts
      if (result.is_dir) {
        folderCount++;
      } else {
        fileCount++;
        // Extensions (only for files)
        const ext = getFileExtension(result.name);
        if (ext) {
          extensionCounts.set(ext, (extensionCounts.get(ext) || 0) + 1);
        }
      }
    });

    // Get top 3 extensions by count
    const topExtensions = Array.from(extensionCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([ext, count]) => ({ ext, count }));

    return {
      volumes: Array.from(volumes).sort(),
      topExtensions,
      fileCount,
      folderCount,
    };
  }, [globalSearchResults]);

  // Apply filters to results
  const filteredResults = useMemo(() => {
    if (selectedVolumes.size === 0 && selectedTypes.size === 0 && selectedExtensions.size === 0) {
      return globalSearchResults;
    }

    return globalSearchResults.filter(result => {
      // Volume filter
      if (selectedVolumes.size > 0 && !selectedVolumes.has(result.volume)) {
        return false;
      }

      // Type filter
      if (selectedTypes.size > 0) {
        const isFolder = result.is_dir;
        if (selectedTypes.has('folder') && !selectedTypes.has('file') && !isFolder) {
          return false;
        }
        if (selectedTypes.has('file') && !selectedTypes.has('folder') && isFolder) {
          return false;
        }
      }

      // Extension filter (only applies to files)
      if (selectedExtensions.size > 0 && !result.is_dir) {
        const ext = getFileExtension(result.name);
        if (!selectedExtensions.has(ext)) {
          return false;
        }
      }

      return true;
    });
  }, [globalSearchResults, selectedVolumes, selectedTypes, selectedExtensions]);

  // Toggle filter helpers
  const toggleVolume = (vol: string) => {
    setSelectedVolumes(prev => {
      const next = new Set(prev);
      if (next.has(vol)) next.delete(vol);
      else next.add(vol);
      return next;
    });
  };

  const toggleType = (type: 'file' | 'folder') => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const toggleExtension = (ext: string) => {
    setSelectedExtensions(prev => {
      const next = new Set(prev);
      if (next.has(ext)) next.delete(ext);
      else next.add(ext);
      return next;
    });
  };

  // Fetch initialized volumes when panel opens
  useEffect(() => {
    if (isGlobalSearchOpen && fs) {
      (async () => {
        try {
          const status = await fs.getSearchEngineStatus();
          if (status.volumes && status.volumes.length > 0) {
            setInitializedVolumes(status.volumes);
          }
        } catch (e) {
          console.error('[GlobalSearch] Failed to fetch search engine status:', e);
        }
      })();
    }
  }, [isGlobalSearchOpen, fs]);

  // Focus input when panel opens.
  // 健壮重试：原实现只 focus 一次，若此刻 pointer lock 仍激活（相对/游戏鼠标模式），
  // 浏览器会忽略 element.focus()，焦点落不到搜索框，导致整会话无法打字（中英文都不行）。
  // 这里逐帧重试直到聚焦成功，并主动释放 pointer lock，不依赖 setModalOpen 的异步释放时机。
  useEffect(() => {
    if (!isGlobalSearchOpen) return;
    let raf = 0;
    let tries = 0;
    const tryFocus = () => {
      const el = inputRef.current;
      if (!el || document.activeElement === el) return; // 已聚焦成功则停止
      // pointer lock 激活时 focus() 会被浏览器忽略，主动释放
      if (document.pointerLockElement) {
        try { document.exitPointerLock(); } catch { /* noop */ }
      }
      el.focus();
      if (document.activeElement !== el && tries++ < 20) {
        raf = requestAnimationFrame(tryFocus); // 每帧重试，上限 ~20 帧防死循环
      }
    };
    const timer = setTimeout(() => { raf = requestAnimationFrame(tryFocus); }, 50);
    return () => { clearTimeout(timer); if (raf) cancelAnimationFrame(raf); };
  }, [isGlobalSearchOpen]);

  // Handle backdrop mouse events for proper close behavior
  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    // 阻止鼠标事件冒泡到 window（InputService 监听在 window 上），避免穿透到远程桌面
    e.nativeEvent.stopImmediatePropagation();
    // Only mark as outside click if mousedown is directly on backdrop (not on panel)
    if (e.target === e.currentTarget) {
      mouseDownOutsideRef.current = true;
    }
  };

  const handleBackdropMouseUp = (e: React.MouseEvent) => {
    e.nativeEvent.stopImmediatePropagation();
    // Only close if both mousedown and mouseup were on backdrop
    if (mouseDownOutsideRef.current && e.target === e.currentTarget) {
      closeGlobalSearch();
    }
    mouseDownOutsideRef.current = false;
  };

  // Debounced search
  useEffect(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (globalSearchQuery.trim().length > 0) {
      const timer = window.setTimeout(() => {
        executeGlobalSearch();
      }, 400);
      setDebounceTimer(timer);
    }
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [globalSearchQuery, globalSearchMode]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeGlobalSearch();
    } else if (e.key === 'Enter') {
      executeGlobalSearch();
    }
  };

  if (!isGlobalSearchOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-start justify-center pt-20 bg-black/50 backdrop-blur-sm"
      data-ui-element="true"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
      onMouseMove={(e) => e.nativeEvent.stopImmediatePropagation()}
      onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
      onContextMenu={(e) => e.nativeEvent.stopImmediatePropagation()}
      onWheel={(e) => e.nativeEvent.stopImmediatePropagation()}
    >
      <div
        ref={panelRef}
        className="w-full max-w-3xl bg-slate-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden"
      >
        {/* Search Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <Search size={20} className="text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              placeholder={t('searchPlaceholder')}
              value={globalSearchQuery}
              onChange={(e) => setGlobalSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent text-lg outline-none placeholder:text-slate-500"
            />
            {globalSearchLoading && (
              <Loader2 size={20} className="text-win-accent animate-spin flex-shrink-0" />
            )}
            {/* Reinit button */}
            <button
              onClick={() => {
                closeGlobalSearch();
                openSearchEngineInitModal(true);
              }}
              title={t('searchEngineReinitTitle')}
              className="p-1.5 hover:bg-white/10 rounded text-slate-400 hover:text-yellow-500 transition-colors"
            >
              <RefreshCw size={16} />
            </button>
            <button onClick={closeGlobalSearch} className="p-1 hover:bg-white/10 rounded">
              <X size={18} />
            </button>
          </div>

          {/* Search Mode Toggle */}
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xs text-slate-500">{t('searchMode')}</span>
            <button
              onClick={() => setGlobalSearchMode('prefix')}
              className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded-full transition-colors ${
                globalSearchMode === 'prefix'
                  ? 'bg-win-accent text-white'
                  : 'bg-white/5 text-slate-400 hover:bg-white/10'
              }`}
            >
              <Type size={12} />
              {t('searchPrefix')}
            </button>
            <button
              onClick={() => setGlobalSearchMode('contains')}
              className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded-full transition-colors ${
                globalSearchMode === 'contains'
                  ? 'bg-win-accent text-white'
                  : 'bg-white/5 text-slate-400 hover:bg-white/10'
              }`}
            >
              <ScanSearch size={12} />
              {t('searchContains')}
            </button>
            <span className="text-xs text-slate-600 ml-2">
              {globalSearchMode === 'prefix' ? t('searchPrefixHint') : t('searchContainsHint')}
            </span>
          </div>

          {/* Initialized Volumes Display */}
          {initializedVolumes.length > 0 && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-slate-500">{t('initializedVolumes') || '已初始化'}</span>
              <div className="flex items-center gap-1.5">
                {initializedVolumes.map(vol => (
                  <span
                    key={vol}
                    className="flex items-center gap-1 px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded"
                  >
                    <HardDrive size={11} />
                    {vol}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Filter Buttons - only show when there are results */}
          {globalSearchResults.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/5">
              {/* Volume filters */}
              {filterOptions.volumes.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <HardDrive size={12} className="text-slate-500" />
                  {filterOptions.volumes.map(vol => (
                    <button
                      key={vol}
                      onClick={() => toggleVolume(vol)}
                      className={`px-2 py-0.5 text-xs rounded transition-colors ${
                        selectedVolumes.has(vol)
                          ? 'bg-blue-500/80 text-white'
                          : 'bg-white/5 text-slate-400 hover:bg-white/10'
                      }`}
                    >
                      {vol}
                    </button>
                  ))}
                </div>
              )}

              {/* Separator */}
              {filterOptions.volumes.length > 0 && (filterOptions.fileCount > 0 || filterOptions.folderCount > 0) && (
                <div className="w-px h-4 bg-white/10" />
              )}

              {/* Type filters */}
              {(filterOptions.fileCount > 0 || filterOptions.folderCount > 0) && (
                <div className="flex items-center gap-1.5">
                  {filterOptions.folderCount > 0 && (
                    <button
                      onClick={() => toggleType('folder')}
                      className={`flex items-center gap-1 px-2 py-0.5 text-xs rounded transition-colors ${
                        selectedTypes.has('folder')
                          ? 'bg-yellow-500/80 text-white'
                          : 'bg-white/5 text-slate-400 hover:bg-white/10'
                      }`}
                    >
                      <FolderOpen size={11} />
                      <span>{filterOptions.folderCount}</span>
                    </button>
                  )}
                  {filterOptions.fileCount > 0 && (
                    <button
                      onClick={() => toggleType('file')}
                      className={`flex items-center gap-1 px-2 py-0.5 text-xs rounded transition-colors ${
                        selectedTypes.has('file')
                          ? 'bg-green-500/80 text-white'
                          : 'bg-white/5 text-slate-400 hover:bg-white/10'
                      }`}
                    >
                      <FileText size={11} />
                      <span>{filterOptions.fileCount}</span>
                    </button>
                  )}
                </div>
              )}

              {/* Separator */}
              {filterOptions.topExtensions.length > 0 && (
                <div className="w-px h-4 bg-white/10" />
              )}

              {/* Extension filters */}
              {filterOptions.topExtensions.length > 0 && (
                <div className="flex items-center gap-1.5">
                  {filterOptions.topExtensions.map(({ ext, count }) => (
                    <button
                      key={ext}
                      onClick={() => toggleExtension(ext)}
                      className={`px-2 py-0.5 text-xs rounded transition-colors ${
                        selectedExtensions.has(ext)
                          ? 'bg-purple-500/80 text-white'
                          : 'bg-white/5 text-slate-400 hover:bg-white/10'
                      }`}
                    >
                      .{ext} <span className="opacity-60">({count})</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Clear filters */}
              {(selectedVolumes.size > 0 || selectedTypes.size > 0 || selectedExtensions.size > 0) && (
                <>
                  <div className="w-px h-4 bg-white/10" />
                  <button
                    onClick={() => {
                      setSelectedVolumes(new Set());
                      setSelectedTypes(new Set());
                      setSelectedExtensions(new Set());
                    }}
                    title={t('searchClearFilters') || 'Clear filters'}
                    className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Results - scrollable container with visible scrollbar */}
        <div
          className="overflow-y-auto overscroll-contain custom-scrollbar"
          style={{ maxHeight: 'calc(60vh - 120px)' }}
          onWheel={(e) => e.stopPropagation()}
        >
          {/* 结果被上限截断的提示：返回条数达到上限，可能还有更多匹配未显示 */}
          {globalSearchTruncated && (
            <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 text-yellow-400 text-xs border-b border-yellow-500/20">
              <span>⚠</span>
              <span>匹配结果过多，仅显示前 200 条，请输入更精确的关键词缩小范围</span>
            </div>
          )}

          {/* Show initializing message with spinner */}
          {globalSearchError && globalSearchStatus === 'initializing' && (
            <div className="p-8 text-center">
              <Loader2 size={32} className="text-win-accent animate-spin mx-auto mb-4" />
              <div className="text-slate-300 mb-2">{globalSearchError}</div>
              <div className="text-xs text-slate-500">{t('searchInitializingHint')}</div>
            </div>
          )}

          {/* Show other errors */}
          {globalSearchError && globalSearchStatus !== 'initializing' && (
            <div className="p-4 text-red-400 text-sm">
              {globalSearchError}
            </div>
          )}

          {!globalSearchLoading && !globalSearchError && globalSearchResults.length === 0 && globalSearchQuery.trim().length >= 2 && (
            <div className="p-8 text-center text-slate-500">
              {t('searchNoResults')}
            </div>
          )}

          {globalSearchQuery.trim().length === 0 && !globalSearchLoading && !globalSearchError && (
            <div className="p-8 text-center text-slate-500">
              {t('searchMinChars')}
            </div>
          )}

          {filteredResults.length > 0 && (
            <div className="divide-y divide-white/5">
              {filteredResults.map((result, idx) => (
                <div
                  key={`${result.frn}-${idx}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 cursor-pointer group transition-colors"
                  onClick={() => navigateToSearchResult(result.full_path)}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    {result.is_dir ? (
                      <Folder size={18} className="text-yellow-400" />
                    ) : (
                      <File size={18} className="text-slate-400" />
                    )}
                  </div>

                  {/* Name and Path */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-200 truncate">
                      {result.name}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {result.full_path}
                    </div>
                  </div>

                  {/* File/Folder Size */}
                  <div className="flex-shrink-0 text-xs text-slate-500 w-20 text-right">
                    {result.is_dir
                      ? formatFileSize(folderSizes[result.full_path]?.total_size)
                      : formatFileSize(result.file_size)}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                    {/* 远程定位：在远程资源管理器中定位并高亮 */}
                    <button
                      title="在远程桌面的资源管理器中定位"
                      className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 bg-white/5 rounded hover:bg-win-accent hover:text-white transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoteOpen(result.full_path, 'select');
                      }}
                    >
                      <MapPin size={12} />
                      远程定位
                    </button>
                    {/* 远程打开：用远程默认程序打开文件/打开文件夹 */}
                    <button
                      title="在远程桌面用默认程序打开"
                      className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 bg-white/5 rounded hover:bg-win-accent hover:text-white transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoteOpen(result.full_path, 'open');
                      }}
                    >
                      <ExternalLink size={12} />
                      远程打开
                    </button>
                    {/* 在 NewWeb 文件管理器中定位 */}
                    <button
                      title="在文件管理器中打开"
                      className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 bg-white/5 rounded hover:bg-win-accent hover:text-white transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigateToSearchResult(result.full_path);
                      }}
                    >
                      {t('searchGoToFolder')}
                      <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No results after filtering */}
          {globalSearchResults.length > 0 && filteredResults.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              {t('searchNoFilteredResults') || 'No results match the selected filters'}
            </div>
          )}
        </div>

        {/* Footer */}
        {globalSearchResults.length > 0 && (
          <div className="px-4 py-2 border-t border-white/10 text-xs text-slate-500 flex justify-between">
            <span>
              {filteredResults.length === globalSearchResults.length
                ? t('searchResultsCount', { count: globalSearchResults.length })
                : `${filteredResults.length} / ${globalSearchResults.length}`}
            </span>
            <span>{actionNotice || t('searchResultsHint')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearchPanel;
